import { useRef, useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import Modal from "../modal/Modal";
import SelectMenuItem from "./SelectMenuItem";
import useTranslation from "../../hooks/useTranslation";
import { TextControl } from "../generic/Core";
import IconButton from "../generic/IconButton";
import { Icon_Plus } from "../../styles/icons";
import { useNavigate } from "react-router-dom";
import { useMainPosition } from "../../hooks/useMainPosition";
import ModalCard from "../modal/ModalCard";
import ReactDOM from "react-dom";
import { useModalStack } from "../../context/modalStackContext";

//Import KeyPress Utils:
import useKeyPress from "../../hooks/useKeyPress";
import resolveDisplay from "./resolveDisplay";
import { evaluateCondition } from "../../../../common/model/condition/evaluate";

const SearchField = styled(TextControl)``;

const ListGroup = styled.ul`
  --list-group-bg: ${({ theme }) => theme.dropdown.background};
  --list-group-border-radius: 0.375rem;
  display: flex;
  flex-direction: column;
  background-color: var(--list-group-bg);
  padding-left: 0;
  margin-bottom: 0;
  border-radius: var(--list-group-border-radius);
  overflow-x: hidden;
  min-height: 40rem;
  list-style: none;
`;

const SearchFieldContainer = styled.div`
    display: flex;
    gap: 0.5rem;
    align-items: center;
`


const SearchResults = styled.div`
  position: absolute;
  top: 60px;
  width: calc(100% - 20px);
  max-height: calc(100% - 60px);
  overflow-y: auto;
  z-index: 101;

  &.visible {
    visibility: visible;
  }

  &.invisible {
    visibility: hidden !important;
  }
`;

// Steps from `from` in `step` direction to the next row `canPick` accepts,
// staying put when there is none further that way.
export const nextSelectable = (list, from, step, canPick) => {
  for (let i = from + step; i >= 0 && i < list.length; i += step) {
    if (canPick(list[i])) return i;
  }
  return from;
};

const Jumper = ({ headers, zIndex }) => {
  const scroll = (id) => {
    const elem = document.getElementById(id);
    if (elem) {
      elem.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Guard against undefined or non-array headers
  if (!headers || !Array.isArray(headers) || headers.length === 0) {
    return null;
  }

  return (
    <JumpWrapper $zIndex={zIndex} style={{ "--jump-count": headers.length }}>
      {headers.map((h) => (
        <JumpItem
          key={h.id}
          onClick={() => {
            scroll(`${h.id}`);
          }}
        >
          {h.name}
        </JumpItem>
      ))}
    </JumpWrapper>
  );
};

// The letters share the wrapper's height evenly, and the font shrinks with
// the letter count (100cqh / count) so a long alphabet never runs past the
// bottom of the modal.
const JumpWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 1rem;
  height: calc(100% - 60px);
  right: 26px;
  top: 60px;
  overflow: hidden;
  container-type: size;
  z-index: ${({ $zIndex }) => $zIndex ? `calc(${$zIndex} + 10)` : '1010'};
`;

const JumpItem = styled.div`
  flex: 0 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 1;
  font-size: min(1.8vh, calc(100cqh / var(--jump-count) * 0.85));
`;



const SelectModal = ({
  data,
  valueName,
  isVisible,
  setIsVisible,
  onSelect,
  entity,
  modalTitle,
  modalOpacity,
  options,
  menuItemField = 'name',
  listDisplay,
  persistValue,
  testId,
  origin = false,
  zIndex,
  onClose,
  onSearch,
}) => {

  const { t } = useTranslation();
  const [cursor, setCursor] = useState(-1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { top, left } = useMainPosition();
  // No longer need isCreateModalVisible state since the modal stack handles visibility

  const searchResultRef = useRef(null);
  const focusRef = useRef(null);
  const navigate = useNavigate();
  const { pushModal } = useModalStack();

  // Handle search input changes - simplified for client-side search
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
  };

  const scrollIntoView = (position) => {
    if (searchResultRef.current) {
      searchResultRef.current.scrollTo({
        top: position,
        behavior: "smooth",
      });
    }
  };

  const clickAction = () => {
    const options={ mode: 'new' }
    // Push the card onto the stack - no need for local state to track visibility
    pushModal(ModalCard, {
      entity,
      options,
      origin,
      isVisible: true, // Always true when pushed to stack
      setIsVisible: () => { }, // No-op since we're using the stack
      modalTitle: 'Add New Record',
      mode: 'fullscreen',
      testId: `${testId}.modalView`,
      zIndex,
      onClose: () => {
        // The modal stack will handle removing the modal
        if (onClose) onClose();
      }
    });
  }

  //On Load: Focus on the search Field
  useEffect(() => {
    focusRef.current?.focus();
  }, []);

  // Debounce search term — filter only runs 150 ms after the user stops typing
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 150);
    return () => clearTimeout(timer);
  }, [search]);

  // Once the term is long enough to search (same 3-char floor as useListData),
  // hand it up to Connect so it can refetch from the server — the picker's
  // initial load is capped at the entity's default page size, so a term that
  // only ever filtered that first page could never reach anything past it.
  useEffect(() => {
    onSearch?.(debouncedSearch);
  }, [debouncedSearch, onSearch]);

  useEffect(() => {
    if (cursor < 0 || cursor > suggestions.length || !searchResultRef.current) {
      return () => { };
    }
    const listItems = Array.from(searchResultRef.current.children);
    listItems[cursor]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [cursor]);

  //Load the value that is already in the DB
  useEffect(() => {
    if (persistValue && valueName) {
      setSearch(valueName);
    } else {
      setSearch("");
    }
  }, [valueName, persistValue]);

  // Below the 3-char floor, `data.list` is still just the entity's first page
  // (no search sent yet), so filter it locally for instant feedback. At 3+
  // chars, Connect has already re-fetched `data.list` from the server via
  // searchString — that search runs against the entity's real searchFields
  // (e.g. glAccount's `number`, not just `name`/`id`), so trust it as-is
  // instead of re-filtering on fields the term was never meant to match.
  const suggestions = useMemo(() => {
    if (!data?.list) return [];
    if (debouncedSearch.length >= 3) return data.list;
    if (!debouncedSearch) return data.list;
    const term = debouncedSearch.toLowerCase();
    return data.list.filter(item => {
      if (item.name?.toLowerCase().includes(term)) return true;
      if (item.subtitle?.toLowerCase().includes(term)) return true;
      if (item.id?.toLowerCase().includes(term)) return true;
      if (typeof listDisplay === 'string') return item[listDisplay]?.toLowerCase().includes(term);
      if (listDisplay?.params) return listDisplay.params.some(f => item[f]?.toLowerCase().includes(term));
      return false;
    });
  }, [data?.list, debouncedSearch, listDisplay]);

  // `options.highlight: { condition }` renders the rows it matches in bold,
  // judged against each row's own data — e.g. a chart of accounts' header and
  // sum accounts. Without a condition nothing is emphasised (evaluateCondition
  // treats a missing condition as true, so it has to be guarded here).
  const highlightCondition = options?.highlight?.condition;
  const isEmphasized = (item) =>
    !!highlightCondition && !item?.header && evaluateCondition(highlightCondition, { values: item });

  // `options.selectable: { condition }` limits which rows can be picked — e.g.
  // only postable accounts. Rows that fail it stay visible (they give the list
  // its structure) but ignore clicks and are skipped by the arrow keys. Here a
  // missing condition holding is what we want: every row is selectable.
  const selectableCondition = options?.selectable?.condition;
  const isDisabled = (item) =>
    !item?.header && !item?.unselectable && !evaluateCondition(selectableCondition, { values: item });
  const canPick = (item) => !!item && !item.header && !item.unselectable && !isDisabled(item);

  // Reset cursor and scroll to top when search or data changes
  useEffect(() => {
    setCursor(-1);
    if (searchResultRef.current) {
      scrollIntoView(0);
    }
  }, [debouncedSearch, data?.list]);



  //Monitoring pressing Enter
  const enterPressed = useKeyPress("Enter");
  const escPressed = useKeyPress("Esc");

  //Keyboard events
  const keyboardNavigation = (e) => {
    // Focus trap: keep focus inside the modal
    if (e.key === "Tab") {
      e.preventDefault();
    }

    // Letter headers, unselectable and disabled rows are skipped — the cursor
    // only ever lands on a row Enter can pick.
    if (e.key === "ArrowDown") {
      setCursor((c) => nextSelectable(suggestions, c, 1, canPick));
    }

    if (e.key === "ArrowUp") {
      setCursor((c) => nextSelectable(suggestions, c, -1, canPick));
    }

    if (e.key === "Escape") {
      setIsVisible(false);
      if (onClose) onClose();
    }

    if (e.key === "Enter" && cursor > 0 && canPick(suggestions[cursor])) {
      setSearch(resolveDisplay(suggestions[cursor], menuItemField, listDisplay));
      setIsVisible(false);
      if (onClose) onClose();
      onSelect(suggestions[cursor]);
    }
  };

  useEffect(() => {
    if (enterPressed) {
      if (cursor >= 0 && canPick(suggestions[cursor])) {
        setSearch(resolveDisplay(suggestions[cursor], menuItemField, listDisplay));
        setIsVisible(false);
        if (onClose) onClose();
        onSelect(suggestions[cursor]);
      }
    }
  }, [enterPressed, suggestions, menuItemField, listDisplay, onClose]);

  useEffect(() => {
    if (escPressed) {
      setIsVisible(false);
      if (onClose) onClose();
    }
  }, [escPressed, onClose]);

  return ReactDOM.createPortal(
    <Modal
      setShow={setIsVisible}
      show={isVisible}
      mode={"fullscreen"}
      className={"fullscreen"}
      minHeight={"35rem"}
      opacity={modalOpacity}
      zIndex={zIndex}
    >
      <Modal.Header setShow={(value) => {
        setIsVisible(value);
        if (!value && onClose) onClose();
      }} onClose={onClose}>
        <h3 style={{ margin: "0px" }}>{t(modalTitle)}</h3>
      </Modal.Header>
      <Modal.Body>
        <SearchFieldContainer>
          <SearchField
            type="text"
            value={search || ""}
            name={`${entity}Search`}
            onChange={handleSearchChange}
            onKeyDown={keyboardNavigation}
            ref={focusRef}
            autoComplete="off"
            data-testid={`${testId}.searchField`}
            aria-label='Search'
            aria-autocomplete='list'
            aria-controls='select-menu'
          />
          <IconButton icon={Icon_Plus} $rounded $variant='primary' $size='2rem' clickAction={clickAction} testId={`${testId}.add`} ariaLabel='Add new record' />
        </SearchFieldContainer>
        <SearchResults>
          <ListGroup ref={searchResultRef} id="select-menu" role="listbox">
            {suggestions.map((item, idx) => (
              <SelectMenuItem
                {...item}
                displayId={options?.displayId || false}
                name={resolveDisplay(item, menuItemField, listDisplay)}
                unselectable={item?.unselectable || false}
                idx={idx}
                key={item.id ?? idx}
                menuItemField={menuItemField}
                disabled={isDisabled(item)}
                onSelectItem={() => {
                  if (!canPick(item)) return;
                  setIsVisible(false);
                  setSearch(resolveDisplay(item, menuItemField, listDisplay));
                  onSelect(item);
                  if (onClose) onClose();
                }}
                isHighlighted={cursor === idx ? true : false}
                emphasized={isEmphasized(item)}
                testId={testId}
              />
            ))}
          </ListGroup>
        </SearchResults>
        {data?.headers && <Jumper headers={data.headers} zIndex={zIndex} />}
      </Modal.Body>
    </Modal>
    , document.body);
};

export default SelectModal;
